/**
 * useQuantumMatrixTools — client implementations of the GLOBAL Quantum Matrix
 * toolset (qm_match / qm_ask / qm_advise), consumed by useFutureBotAgent.
 *
 * Everything QM goes through the queryQuantumMatrix callable (the only holder
 * of the API key). This hook owns the parts the model must never manage:
 *
 *  - Multi-turn state. QM has no server-side sessions — continuity works by
 *    echoing the previous result's `merged_parameters` verbatim on the next
 *    ask (plus up to 20 prior {role, content} messages). That state lives
 *    here, per chat session, persisted to localStorage so a reload mid-
 *    conversation keeps the thread (matching transcript persistence).
 *  - Job polling. `ask` (and the advisory final answer) are async jobs that
 *    run 20–95s; we poll every 4s up to 240s and bail early when the user
 *    hits Stop (the agent loop's cancelRef).
 *  - Response trimming. A raw match can carry 122 full program rows — far
 *    past the token budget. Results are cut to counts + grouped highlights
 *    with "+N more" markers before they reach the model.
 *
 * QM's 4xx payloads (valid enum values, the known-lender list) are returned
 * as { error, details } tool results so the model can self-correct.
 */

import { useCallback, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const QM_STATE_KEY_PREFIX = 'futureBotQmState:';
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 240000;
const CANCEL_CHECK_MS = 250;
const QM_MESSAGES_MAX = 20;
// Keep stored assistant messages a bit lean; QM truncates at 8000 server-side.
const QM_MESSAGE_CONTENT_MAX = 6000;

const MATCH_MAX_LENDERS = 12;
const MATCH_MAX_PROGRAMS_PER_LENDER = 4;
const ASK_MAX_PROGRAMS = 15;
const NOTES_MAX_CHARS = 220;
const CONTEXT_TEXT_MAX_CHARS = 400;
const DECISION_PATH_MAX = 8;

// ---------------------------------------------------------------------------
// Session state persistence
// ---------------------------------------------------------------------------

function emptyQmState() {
  return { previousParameters: null, messages: [], advisory: null };
}

function loadQmState(sessionId) {
  try {
    const raw = localStorage.getItem(`${QM_STATE_KEY_PREFIX}${sessionId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      return {
        previousParameters:
          parsed.previousParameters && typeof parsed.previousParameters === 'object'
            ? parsed.previousParameters
            : null,
        messages: Array.isArray(parsed.messages) ? parsed.messages : [],
        advisory:
          parsed.advisory && typeof parsed.advisory === 'object' ? parsed.advisory : null,
      };
    }
  } catch {
    /* corrupted state just starts fresh */
  }
  return emptyQmState();
}

function saveQmState(sessionId, state) {
  try {
    localStorage.setItem(`${QM_STATE_KEY_PREFIX}${sessionId}`, JSON.stringify(state));
  } catch {
    /* quota errors are non-fatal */
  }
}

function removeQmState(sessionId) {
  try {
    localStorage.removeItem(`${QM_STATE_KEY_PREFIX}${sessionId}`);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Response trimming (exported for tests)
// ---------------------------------------------------------------------------

function truncate(text, max) {
  if (typeof text !== 'string') return undefined;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function trimProgram(p) {
  if (!p || typeof p !== 'object') return p;
  const out = {
    lender_name: p.lender_name,
    program_name: p.program_name,
    program_category: p.program_category,
    program_type: p.program_type,
    min_fico: p.min_fico,
    max_ltv: p.max_ltv,
    min_loan_amount: p.min_loan_amount,
    max_loan_amount: p.max_loan_amount,
    notes: truncate(p.notes, NOTES_MAX_CHARS),
  };
  if (p.pdf_url) out.pdf_url = p.pdf_url;
  return out;
}

/** match response -> counts, filters, and grouped highlights. */
export function trimMatchResponse(body) {
  const lenders = Array.isArray(body.lenders) ? body.lenders : [];
  const out = {
    total_matches: body.total_matches,
    unique_lenders: body.unique_lenders,
    parameters_used: body.parameters_used,
    query_filters_applied: body.query_filters_applied,
    warnings: Array.isArray(body.warnings) && body.warnings.length ? body.warnings : undefined,
    lenders: lenders.slice(0, MATCH_MAX_LENDERS).map((l) => {
      const programs = Array.isArray(l.programs) ? l.programs : [];
      const entry = {
        lender_name: l.lender_name,
        program_count: l.program_count,
        programs: programs.slice(0, MATCH_MAX_PROGRAMS_PER_LENDER).map(trimProgram),
      };
      if (programs.length > MATCH_MAX_PROGRAMS_PER_LENDER) {
        entry.more_programs_not_shown = programs.length - MATCH_MAX_PROGRAMS_PER_LENDER;
      }
      return entry;
    }),
  };
  if (lenders.length > MATCH_MAX_LENDERS) {
    out.more_lenders_not_shown = lenders.length - MATCH_MAX_LENDERS;
    out.note = `Showing the first ${MATCH_MAX_LENDERS} of ${lenders.length} lenders. Scope with lender/lenders or add scenario fields to narrow.`;
  }
  return out;
}

/** completed ask result (type answer/recommendation) -> model-sized payload. */
export function trimAskResult(result) {
  const metadata = result.metadata || {};
  const context = result.context || {};
  const programs = Array.isArray(result.matched_programs) ? result.matched_programs : [];
  const out = {
    answer: result.answer,
    intent: result.intent || metadata.intent,
    answer_mode: result.answer_mode || metadata.answer_mode,
    programs_referenced: metadata.programs_referenced,
    total_matches: context.total_matches,
    query_filters_applied: context.query_filters_applied,
    warnings: Array.isArray(metadata.warnings) && metadata.warnings.length ? metadata.warnings : undefined,
  };
  if (programs.length) {
    out.matched_programs = programs.slice(0, ASK_MAX_PROGRAMS).map(trimProgram);
    if (programs.length > ASK_MAX_PROGRAMS) {
      out.more_programs_not_shown = programs.length - ASK_MAX_PROGRAMS;
    }
  }
  return out;
}

/** advisory followup (from ask result or advise response) -> relay payload. */
export function trimAdvisoryFollowup(followup) {
  return {
    advisory_followup: true,
    message: followup.message,
    scenario_display: followup.scenario_display,
    progress: followup.progress,
    questions: (Array.isArray(followup.questions) ? followup.questions : []).map((q) => ({
      question_key: q.question_key,
      question_text: q.question_text,
      context_text: truncate(q.context_text, CONTEXT_TEXT_MAX_CHARS),
      response_type: q.response_type,
      response_options: (Array.isArray(q.response_options) ? q.response_options : []).map(
        (o) => ({ label: o.label, value: o.value })
      ),
      is_required: q.is_required,
      allow_skip: q.allow_skip,
    })),
    next_step:
      'Relay these questions to the user (context_text explains why each matters — share it). ' +
      'Collect their answers, then call qm_advise with answers keyed by question_key using the option VALUES.',
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {string} opts.sessionId  current chat session (QM state is per-session)
 * @param {{current: boolean}} opts.cancelRef  the agent loop's Stop flag
 */
export function useQuantumMatrixTools({ sessionId, cancelRef }) {
  // Lazily (re)load state when the session changes; keep it in a ref so tool
  // closures always see fresh state without re-rendering the chat.
  const stateRef = useRef({ sessionId: null, state: null });
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const getState = useCallback(() => {
    const sid = sessionIdRef.current;
    if (stateRef.current.sessionId !== sid || !stateRef.current.state) {
      stateRef.current = { sessionId: sid, state: loadQmState(sid) };
    }
    return stateRef.current.state;
  }, []);

  const patchState = useCallback(
    (updates) => {
      const next = { ...getState(), ...updates };
      stateRef.current = { sessionId: sessionIdRef.current, state: next };
      saveQmState(sessionIdRef.current, next);
      return next;
    },
    [getState]
  );

  /** Store a completed exchange: parameters echo + rolling message history. */
  const recordExchange = useCallback(
    ({ mergedParameters, userContent, assistantContent }) => {
      const state = getState();
      const messages = [...state.messages];
      if (userContent) messages.push({ role: 'user', content: userContent.slice(0, QM_MESSAGE_CONTENT_MAX) });
      if (assistantContent) {
        messages.push({ role: 'assistant', content: assistantContent.slice(0, QM_MESSAGE_CONTENT_MAX) });
      }
      patchState({
        ...(mergedParameters && typeof mergedParameters === 'object'
          ? { previousParameters: mergedParameters }
          : {}),
        messages: messages.slice(-QM_MESSAGES_MAX),
      });
    },
    [getState, patchState]
  );

  const resetSession = useCallback(() => {
    removeQmState(sessionIdRef.current);
    stateRef.current = { sessionId: sessionIdRef.current, state: emptyQmState() };
  }, []);

  // --- proxy plumbing --------------------------------------------------------

  const callProxy = useCallback(async (payload) => {
    // Resolved lazily (not at module scope) so importing this file never
    // requires an initialized Firebase app — getFunctions caches per app.
    const callable = httpsCallable(getFunctions(undefined, 'us-east1'), 'queryQuantumMatrix', {
      timeout: 70000,
    });
    const { data } = await callable(payload);
    return data; // { ok, status, data }
  }, []);

  /** QM/proxy error -> tool-result error the model can act on. */
  const relayError = useCallback((res) => {
    const body = res && res.data ? res.data : {};
    const out = { error: body.error || `Quantum Matrix request failed (HTTP ${res ? res.status : '?'}).` };
    if (body.details) out.details = body.details;
    return out;
  }, []);

  const sleepUnlessCancelled = useCallback(
    async (ms) => {
      const until = Date.now() + ms;
      while (Date.now() < until) {
        if (cancelRef && cancelRef.current) return false;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, Math.min(CANCEL_CHECK_MS, until - Date.now())));
      }
      return !(cancelRef && cancelRef.current);
    },
    [cancelRef]
  );

  /**
   * Poll a QM job to completion.
   * Resolves { result } | { error } | { cancelled: true } | { timeout: true }.
   */
  const pollJob = useCallback(
    async (jobId, externalRef) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let consecutiveFailures = 0;
      for (;;) {
        if (Date.now() > deadline) return { timeout: true };
        // eslint-disable-next-line no-await-in-loop
        const alive = await sleepUnlessCancelled(POLL_INTERVAL_MS);
        if (!alive) return { cancelled: true };
        let res;
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await callProxy({ action: 'job', job_id: jobId, external_ref: externalRef });
        } catch (err) {
          res = null;
        }
        if (!res || !res.ok) {
          // 404 = bad job id, give up; transient blips keep polling (≤3 in a row).
          if (res && res.status === 404) return { error: 'Quantum Matrix job not found.' };
          consecutiveFailures += 1;
          if (consecutiveFailures >= 3) {
            return { error: (res && res.data && res.data.error) || 'Lost contact with the Quantum Matrix while polling.' };
          }
          continue;
        }
        consecutiveFailures = 0;
        const body = res.data || {};
        if (body.status === 'completed') return { result: body.result || {} };
        if (body.status === 'failed') {
          return { error: body.error || 'The Quantum Matrix job failed.' };
        }
        // pending / processing -> keep polling
      }
    },
    [callProxy, sleepUnlessCancelled]
  );

  // --- shared result handling ------------------------------------------------

  /** Handle a completed ask-style job result (answer / advisory / recommendation). */
  const handleAskResult = useCallback(
    (result, { question }) => {
      if (result.type === 'advisory_followup') {
        patchState({
          advisory: {
            scenarioType: result.scenario_type,
            scenarioDisplay: result.scenario_display,
            gatheredParams: result.gathered_params || {},
          },
          ...(result.merged_parameters ? { previousParameters: result.merged_parameters } : {}),
        });
        return trimAdvisoryFollowup(result);
      }
      // type "answer" (and defensive default for anything answer-shaped)
      recordExchange({
        mergedParameters: result.merged_parameters,
        userContent: question,
        assistantContent: result.answer,
      });
      return trimAskResult(result);
    },
    [patchState, recordExchange]
  );

  // --- tool implementations ---------------------------------------------------

  const qmMatch = useCallback(
    async (args = {}) => {
      const { include_documents: includeDocuments, deal_id: dealId, ...dealFields } = args;
      const payload = {
        action: 'match',
        deal: dealFields,
        loan_amount_mode: 'permissive',
        ...(typeof includeDocuments === 'boolean' ? { include_documents: includeDocuments } : {}),
        external_ref: dealId || sessionIdRef.current,
      };
      const res = await callProxy(payload);
      if (!res.ok) return relayError(res);
      return trimMatchResponse(res.data || {});
    },
    [callProxy, relayError]
  );

  const qmAsk = useCallback(
    async (args = {}) => {
      if (args.new_scenario) {
        // The user switched borrowers/deals: drop parameters, history, and any
        // in-flight advisory so the next answer starts clean.
        patchState(emptyQmState());
      }
      const state = getState();
      const filters = {};
      if (Array.isArray(args.lenders) && args.lenders.length) filters.lenders = args.lenders;
      if (Array.isArray(args.categories) && args.categories.length) filters.categories = args.categories;
      if (args.agency_scope) filters.scope = 'agency';
      if (args.agency_source) filters.agency_source = args.agency_source;

      const payload = {
        action: 'ask',
        question: args.question,
        previous_parameters: state.previousParameters,
        messages: state.messages,
        ...(Object.keys(filters).length ? { filters } : {}),
        response_mode: args.response_mode || 'concise',
        external_ref: sessionIdRef.current,
      };

      const submit = await callProxy(payload);
      if (!submit.ok) return relayError(submit);
      const jobId = submit.data && submit.data.job_id;
      if (!jobId) return { error: 'Quantum Matrix did not return a job id.', details: submit.data };

      const polled = await pollJob(jobId, sessionIdRef.current);
      if (polled.cancelled) return { error: 'Cancelled by the user while waiting for the Quantum Matrix.' };
      if (polled.timeout) {
        return { error: 'The Quantum Matrix is taking longer than 4 minutes. Tell the user it timed out and offer to try again.' };
      }
      if (polled.error) return { error: polled.error };
      return handleAskResult(polled.result, { question: args.question });
    },
    [callProxy, relayError, pollJob, getState, patchState, handleAskResult]
  );

  const qmAdvise = useCallback(
    async (args = {}) => {
      const state = getState();
      const advisory = state.advisory;
      const hasAnswers = args.answers && typeof args.answers === 'object' && Object.keys(args.answers).length > 0;

      let payload;
      if (hasAnswers && advisory) {
        payload = {
          action: 'advise',
          scenario_type: advisory.scenarioType,
          scenario_display: advisory.scenarioDisplay,
          scenario_responses: args.answers,
          gathered_params: { ...(advisory.gatheredParams || {}), ...args.answers },
          response_mode: 'concise',
          external_ref: sessionIdRef.current,
        };
      } else if (typeof args.user_message === 'string' && args.user_message.trim()) {
        payload = {
          action: 'advise',
          user_message: args.user_message,
          response_mode: 'concise',
          external_ref: sessionIdRef.current,
        };
      } else if (hasAnswers) {
        return {
          error:
            'No advisory is in progress for this conversation, so there are no questions these answers belong to. ' +
            'Start with qm_ask (it escalates to advisory automatically) or qm_advise with user_message.',
        };
      } else {
        return { error: 'qm_advise needs answers (mid-advisory) or user_message (to start one).' };
      }

      const res = await callProxy(payload);
      if (!res.ok) return relayError(res);
      const body = res.data || {};
      const advise = body.advise || {};

      if (advise.action === 'followup') {
        patchState({
          advisory: {
            scenarioType: advise.scenario_type,
            scenarioDisplay: advise.scenario_display,
            gatheredParams: advise.gathered_params || {},
          },
        });
        return trimAdvisoryFollowup(advise);
      }

      if (advise.action === 'recommend') {
        let final = null;
        if (body.answer_job_id) {
          const polled = await pollJob(body.answer_job_id, sessionIdRef.current);
          if (polled.cancelled) {
            return { error: 'Cancelled by the user while the final recommendation answer was generating.' };
          }
          if (polled.result) final = polled.result;
          // timeout/error: fall through with the structured recommendation only
        }

        patchState({ advisory: null });
        if (final) {
          recordExchange({
            mergedParameters: final.merged_parameters,
            userContent: payload.user_message || null,
            assistantContent: final.answer,
          });
        }

        const routing = advise.routing_context || {};
        const out = {
          recommendation: true,
          scenario_summary: advise.scenario_summary,
          eligibility_dates: routing.eligibility_dates,
          decision_path: (Array.isArray(advise.decision_path) ? advise.decision_path : [])
            .slice(0, DECISION_PATH_MAX)
            .map((d) => ({ rule: d.rule, outcome: d.outcome })),
        };
        if (final && final.answer) {
          out.answer = final.answer;
          const trimmed = trimAskResult(final);
          if (trimmed.programs_referenced) out.programs_referenced = trimmed.programs_referenced;
          if (trimmed.matched_programs) out.matched_programs = trimmed.matched_programs;
        } else {
          out.note =
            'The grounded final answer was not available in time — present the recommendation, eligibility dates, and decision path above.';
        }
        return out;
      }

      return { error: 'Unexpected advisory response from the Quantum Matrix.', details: body };
    },
    [callProxy, relayError, pollJob, getState, patchState, recordExchange]
  );

  const tools = useMemo(
    () => ({ qm_match: qmMatch, qm_ask: qmAsk, qm_advise: qmAdvise }),
    [qmMatch, qmAsk, qmAdvise]
  );

  return { tools, resetSession };
}

export default useQuantumMatrixTools;
